// Radha — jurisdiction settings (063/064/065). Without this panel the whole
// Phase-1 globalization stack is unreachable: the columns exist and the guard
// reads them, but nobody can set them.
//
// The four fields are presented as one choice because they are one choice —
// picking a market implies its currency, its zone and its formatting. Advanced
// users can still override each part (a US company reporting in EUR, say), but
// the common path is one dropdown.
import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { useSetJurisdiction } from "../hooks/useTenantSettings";
import { writeErrorMessage } from "../hooks/useCollectionsWrites";
import type { Me } from "../lib/authSupabase";

// The first-wave markets. Each preset is the conventional pairing; every field
// stays individually editable below.
const MARKETS: { country: string; label: string; currency: string; timezone: string; locale: string }[] = [
  { country: "IN", label: "India",          currency: "INR", timezone: "Asia/Kolkata",      locale: "en-IN" },
  { country: "US", label: "United States",  currency: "USD", timezone: "America/New_York",  locale: "en-US" },
  { country: "GB", label: "United Kingdom", currency: "GBP", timezone: "Europe/London",     locale: "en-GB" },
  { country: "DE", label: "Germany",        currency: "EUR", timezone: "Europe/Berlin",     locale: "de-DE" },
  { country: "FR", label: "France",         currency: "EUR", timezone: "Europe/Paris",      locale: "fr-FR" },
  { country: "NL", label: "Netherlands",    currency: "EUR", timezone: "Europe/Amsterdam",  locale: "nl-NL" },
  { country: "ES", label: "Spain",          currency: "EUR", timezone: "Europe/Madrid",     locale: "es-ES" },
  { country: "AE", label: "United Arab Emirates", currency: "AED", timezone: "Asia/Dubai",  locale: "ar-AE" },
];

interface ConductRule {
  country: string; regime_name: string; is_statutory: boolean; citation: string | null;
  window_start_hour: number; window_end_hour: number; rest_days: number[];
  max_contacts_per_day: number | null; max_contacts_per_7_days: number | null; notes: string | null;
}

function useConductRule(country: string) {
  return useQuery({
    queryKey: ["conduct-rule", country],
    queryFn: async (): Promise<ConductRule | null> => {
      const { data, error } = await supabase
        .from("collection_conduct_rules")
        .select("country, regime_name, is_statutory, citation, window_start_hour, window_end_hour, rest_days, max_contacts_per_day, max_contacts_per_7_days, notes")
        .eq("country", country).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!country,
  });
}

const DOW = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function JurisdictionSettings({ me, allowed }: { me: Me | null; allowed: boolean }) {
  const save = useSetJurisdiction();
  const [country, setCountry] = useState("IN");
  const [currency, setCurrency] = useState("INR");
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [locale, setLocale] = useState("en-IN");
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const rule = useConductRule(country);

  useEffect(() => {
    if (!me) return;
    setCountry(me.tenant_country); setCurrency(me.tenant_base_currency);
    setTimezone(me.tenant_timezone); setLocale(me.tenant_locale);
  }, [me?.tenant_country, me?.tenant_base_currency, me?.tenant_timezone, me?.tenant_locale]);

  const pickMarket = (cc: string) => {
    const m = MARKETS.find((x) => x.country === cc);
    if (!m) return;
    setCountry(m.country); setCurrency(m.currency); setTimezone(m.timezone); setLocale(m.locale);
  };

  const onSave = () => {
    setNote(null);
    save.mutate({ country, base_currency: currency, timezone, locale }, {
      // A jurisdiction change alters money formatting everywhere and the legal
      // contact window — a reload is the honest way to re-derive all of it.
      onSuccess: () => { setNote({ ok: true, text: "Saved — reloading to apply" }); setTimeout(() => window.location.reload(), 900); },
      onError: (e) => setNote({ ok: false, text: writeErrorMessage(e) }),
    });
  };

  const inp: React.CSSProperties = {
    padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13,
    background: allowed ? "var(--surface)" : "var(--surface-2)", color: "var(--fg)",
  };
  const r = rule.data;

  return (
    <>
      <div style={{ font: "600 15px Inter", margin: "28px 0 6px" }}>Jurisdiction</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "0 0 14px" }}>
        Where this entity operates. This drives how money and dates are formatted throughout Radha, which language
        reminders default to, and — most importantly — the legal contact window the collections guard enforces before
        letting a reminder go out.
      </p>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 13, color: "var(--fg-muted)" }}>Market</span>
        <select value={country} onChange={(e) => pickMarket(e.target.value)} disabled={!allowed} style={{ ...inp, minWidth: 190 }}>
          {MARKETS.map((m) => <option key={m.country} value={m.country}>{m.label}</option>)}
        </select>
        {allowed && (
          <button onClick={onSave} disabled={save.isPending}
            style={{ padding: "7px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 12.5, color: "var(--fg-muted)" }}>Reporting currency</span>
        <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
          disabled={!allowed} style={{ ...inp, width: 78 }} />
        <span style={{ fontSize: 12.5, color: "var(--fg-muted)" }}>Time zone</span>
        <input value={timezone} onChange={(e) => setTimezone(e.target.value)} disabled={!allowed} style={{ ...inp, width: 190 }} />
        <span style={{ fontSize: 12.5, color: "var(--fg-muted)" }}>Locale</span>
        <input value={locale} onChange={(e) => setLocale(e.target.value)} disabled={!allowed} style={{ ...inp, width: 96 }} />
      </div>
      {note && <p style={{ fontSize: 13, color: note.ok ? "var(--status-good)" : "var(--status-critical)", margin: "0 0 10px" }}>{note.text}</p>}

      {r && (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderLeft: `3px solid ${r.is_statutory ? "var(--status-critical)" : "var(--fg-faint)"}`, borderRadius: 4, padding: "12px 16px", marginTop: 4 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <strong style={{ fontSize: 13.5 }}>{r.regime_name}</strong>
            <span style={{ fontSize: 10.5, letterSpacing: ".06em", textTransform: "uppercase", padding: "1px 6px", borderRadius: 3, border: "1px solid currentColor", color: r.is_statutory ? "var(--status-critical)" : "var(--fg-muted)" }}>
              {r.is_statutory ? "Required by law" : "Your policy"}
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: "var(--fg-soft, var(--fg-muted))", marginTop: 6 }}>
            Contact allowed {String(r.window_start_hour).padStart(2, "0")}:00–{String(r.window_end_hour).padStart(2, "0")}:00 local
            {r.max_contacts_per_day ? ` · max ${r.max_contacts_per_day}/account/day` : ""}
            {r.max_contacts_per_7_days ? ` · max ${r.max_contacts_per_7_days}/account/7 days` : ""}
            {r.rest_days?.length ? ` · not on ${r.rest_days.map((d) => DOW[d]).join(", ")}` : ""}
          </div>
          {r.notes && <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 6, lineHeight: 1.5 }}>{r.notes}</div>}
          {r.citation && <div style={{ fontSize: 11.5, color: "var(--fg-faint)", marginTop: 4 }}>{r.citation}</div>}
        </div>
      )}
    </>
  );
}
